import { Link, useParams } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faArrowLeft,
  faComments,
  faQrcode,
  faUsers,
} from "@fortawesome/free-solid-svg-icons"
import ApiError from "../../components/ApiError"
import StatTile from "../../components/StatTile"
import EventQrPreview from "../../components/EventQrPreview"
import FeedbackResponseCard from "../../components/FeedbackResponseCard"
import routePaths from "../../constants/path"
import { useEventQrCodeSummary } from "../../hooks/useEventQr"
import { getVideoProgress } from "../EducationalVideoLogs/videoMetrics"
import EventDescription from "./EventDescription"
import EventScansChart from "./EventScansChart"
import { EventStatusPill, formatEventDate } from "./shared"

const Card = ({ title, children, className = "" }) => (
  <section
    className={`rounded-2xl border border-gray-200 bg-white p-4 shadow-sm ${className}`}
  >
    {title && (
      <h2 className="mb-3 text-sm font-semibold text-gray-900">{title}</h2>
    )}
    {children}
  </section>
)

const Empty = ({ children }) => (
  <p className="py-8 text-center text-sm text-gray-400">{children}</p>
)

/*
  Across every attendee, so the bar is the furthest anyone got — not one
  person's progress like the report view. A video counts as played as soon as
  someone presses play; below ViewMedica's first 25% milestone the position
  comes from play/pause events, or reads "started" when there is none.
*/
const VideoRow = ({ playback }) => {
  const {
    isViewed,
    isStartedOnly,
    percent,
    barPercent,
    isComplete,
    watchCount,
    completedPlayCount,
  } = getVideoProgress(playback)

  const plays = `${watchCount} ${watchCount === 1 ? "play" : "plays"}`
  const progress = isStartedOnly
    ? "started · under 25%"
    : `${completedPlayCount} finished · furthest ${percent}%`

  return (
    <div className="py-2">
      <div className="flex items-center justify-between gap-3 text-sm">
        <span
          className={`truncate ${isViewed ? "font-medium text-gray-800" : "text-gray-500"}`}
          title={playback.videoTitle}
        >
          {playback.videoTitle || `Video ${playback.videoId}`}
        </span>
        <span className="shrink-0 text-xs tabular-nums text-gray-500">
          {isViewed ? `${plays} · ${progress}` : "not played yet"}
        </span>
      </div>
      <div
        aria-hidden="true"
        className="mt-1.5 h-[3px] overflow-hidden rounded-full bg-gray-200"
      >
        <div
          className={`h-full rounded-full ${isComplete ? "bg-green-500" : "bg-secondary"}`}
          style={{ width: `${barPercent}%` }}
        />
      </div>
    </div>
  )
}

const EventQrCodeDetail = () => {
  const { id } = useParams()
  const { data, isLoading, isError, error } = useEventQrCodeSummary(id)

  if (isError) {
    return (
      <div className="m-4">
        <ApiError axiosError={error} />
      </div>
    )
  }

  if (isLoading || !data) {
    return <div className="m-8 text-center text-sm text-gray-500">Loading…</div>
  }

  const { event, summary, playbackSummary, feedback } = data

  return (
    <div className="mx-4 my-4">
      <Link
        className="mb-3 inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800"
        to={routePaths.eventQrCodes}
      >
        <FontAwesomeIcon className="h-3 w-3" icon={faArrowLeft} />
        Event QR Codes
      </Link>

      <div className="mb-5">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-xl font-medium text-primary">
            {event.eventName}
          </h1>
          <EventStatusPill status={event.status} />
          <span className="text-sm text-gray-500">
            {formatEventDate(event.startsAt, "Live from creation")} →{" "}
            {formatEventDate(event.expiresAt, "no expiry")}
          </span>
        </div>
        {event.description && <EventDescription text={event.description} />}
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_280px]">
        <div className="min-w-0 space-y-4">
          <div className="flex flex-wrap gap-3">
            <StatTile
              accent="blue"
              icon={faQrcode}
              label="Total scans"
              value={summary.totalScans}
            />
            <StatTile
              accent="violet"
              hint="Distinct browsers. The same person on two phones counts twice."
              icon={faUsers}
              label="Unique visitors"
              value={summary.uniqueVisitors}
            />
            <StatTile
              accent="amber"
              icon={faComments}
              label="Feedback"
              value={summary.feedbackCount}
            />
          </div>

          <EventScansChart event={event} />

          <Card title="Video engagement">
            {playbackSummary.length ? (
              <div className="divide-y divide-gray-100">
                {playbackSummary.map((playback) => (
                  <VideoRow key={playback.videoId} playback={playback} />
                ))}
              </div>
            ) : (
              <Empty>No videos on this event.</Empty>
            )}
          </Card>

          <Card title={`Feedback (${feedback.length})`}>
            {feedback.length ? (
              <div className="grid gap-3 md:grid-cols-2">
                {feedback.map((response, index) => (
                  <FeedbackResponseCard
                    key={response.EventCustomerFeedbackId}
                    index={feedback.length - 1 - index}
                    response={response}
                  />
                ))}
              </div>
            ) : (
              <Empty>No feedback yet.</Empty>
            )}
          </Card>
        </div>

        <Card className="h-fit" title="QR code">
          <EventQrPreview
            eventName={event.eventName}
            publicUuid={event.publicUuid}
          />
        </Card>
      </div>
    </div>
  )
}

export default EventQrCodeDetail
